import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';

import { steps } from './stepDefinitions';
import QuestionStep from './step/QuestionStep';
import MovieLoading from '../utils/Loading';

const Wizard = () => {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();

  // Parse answers from URL query parameter 'q' (comma-separated)
  const answers = useMemo(() => {
    const q = searchParams.get('q');
    return q ? q.split(',') : [];
  }, [searchParams]);

  // Current step index equals number of answers given so far
  const currentStepIndex = answers.length;
  const currentStep = steps[currentStepIndex];

  // Local UI states for option fetching
  const [options, setOptions] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  // If all steps are already answered, navigate to search immediately
  useEffect(() => {
    if (answers.length === steps.length) {
      navigate(`/search?tags=${answers.join(',')}`, { replace: true });
    }
  }, [answers, navigate, steps.length]);

  // Fetch options whenever the current step or answers change
  useEffect(() => {
    // No step to fetch for (e.g., completed)
    if (!currentStep) return;

    const fetchOptions = async () => {
      setLoading(true);
      setError(null);
      try {
        const result = await currentStep.getOptions(answers);
        setOptions(result);
      } catch (err) {
        setError(err.message);
      } finally {
        setLoading(false);
      }
    };

    fetchOptions();
  }, [currentStep, answers]); // Re-run when step or answers change

  // Navigate to the next step or complete the wizard
  const goToNext = useCallback((selectedValue) => {
    const newAnswers = [...answers, selectedValue];

    if (newAnswers.length === steps.length) {
      // All steps completed -> go to search page
      navigate(`/search?tags=${newAnswers.join(',')}`);
    } else {
      // Update URL with new answers
      setSearchParams(prev => {
        prev.set('q', newAnswers.join(','));
        return prev;
      });
    }
  }, [answers, navigate, searchParams, steps.length]);

  // Go back one step (remove last answer)
  const goBack = useCallback(() => {
    if (answers.length === 0) return;

    const newAnswers = answers.slice(0, -1);
    setSearchParams(prev => {
      if (newAnswers.length > 0) {
        prev.set('q', newAnswers.join(','));
      } else {
        prev.delete('q');
      }
      return prev;
    });
  }, [answers, navigate, searchParams, steps.length]);

  // Jump to a specific step index (truncate answers)
  const goToStep = useCallback((index) => {
    if (index < 0 || index >= steps.length) return;

    const newAnswers = answers.slice(0, index);
    setSearchParams(prev => {
      if (newAnswers.length > 0) {
        prev.set('q', newAnswers.join(','));
      } else {
        prev.delete('q');
      }
      return prev;
    });
  }, [answers, searchParams, steps.length]);

  // Reset the wizard (clear answers)
  const reset = useCallback(() => {
    setSearchParams(prev => {
      prev.delete('q');
      return prev;
    });
  }, [searchParams]);

  // Render loading/error states, or the current question step
  if (loading) return <MovieLoading />;
  if (error) return <div className="error">Error: {error}</div>;

  // If no current step (e.g., after completion redirection), render nothing
  if (!currentStep) return null;

  return (
    <QuestionStep
      question={currentStep}
      options={options}
      onSelect={goToNext}
      onBack={goBack}
      stepNumber={currentStepIndex}
      totalSteps={steps.length}
    />
  );
};

export default Wizard;
